import { AxiosError } from "axios";
import { defineStore } from "pinia";
import { ngFrameworkLoaded } from "@sazka/ng/ng-integration";
import { useNgSession } from "@sazka/ng/ng-session";
import { getPlayerAccount } from "@sazka/ng/player/http-clients/player-account-http-client";
import { PlayerAccountStatusValue } from "@sazka/ng/player/model/player-account-response";
import { UrlUtils } from "@sazka/utils/url";
import { BiRealtimeHttpMetods } from "@/shared/http-clients/bi-realtime/bi-realtime-http-methods";
import { cancelIdScan, postVerification, verifyIdScan } from "../api/registration-api";
import { VerticalId } from "../models/registration-configuration-models";
import { RegistrationStep } from "../models/registration-step";
import { RegistrationUnsuccessReason } from "../models/registration-unsuccess-reason";
import { RegistrationEcode, showRegistrationError } from "../utils/errors";

let promiseResolve;

export const useRegistrationStore = defineStore("registration", {
    state: () => ({
        user: {
            firstName: undefined as string | undefined,
            lastName: undefined as string | undefined,
            street: undefined as string | undefined,
            city: undefined as string | undefined,
            zipCode: undefined as string | undefined,
            phone: undefined as string | undefined,
            email: undefined as string | undefined,
            registrationVertical: undefined as VerticalId | undefined,
            isLimitedAccount: undefined as boolean | undefined,
            hid: undefined as string | undefined,
            citizenship: undefined as string | undefined,
        },
        progress: {
            isPersonalDataFilled: undefined as boolean | undefined,
            isSourceOfFundsVerified: undefined as boolean | undefined,
            areRegistersChecked: undefined as boolean | undefined,
            isBankAccountVerified: undefined as boolean | undefined,
            isEmailVerified: undefined as boolean | undefined,
            isPersonalDataVerified: undefined as boolean | undefined,
            areGamingLimitsSet: undefined as boolean | undefined,
            isDepositApproved: undefined as boolean | undefined,
            isDepositPending: undefined as boolean | undefined,
            isDepositDeclined: undefined as boolean | undefined,
            isOcrPending: undefined as boolean | undefined,
            isOcrApproved: undefined as boolean | undefined,
        },
        returnUrl: undefined as string | undefined,
        isWaitingForDeposit: false,
        activeStep: undefined as RegistrationStep,
        unsuccessReason: undefined as RegistrationUnsuccessReason,
        selectedBonusId: undefined as number,
        isDataSetPromise: new Promise(function (resolve) {
            promiseResolve = resolve;
        }),
        bankIdSelectedBank: undefined as string,
    }),
    getters: {
        availableStep(state): RegistrationStep {
            if (!state.progress.isPersonalDataFilled) {
                return RegistrationStep.PersonalDataFillingChoice;
            }
            if (!state.progress.isSourceOfFundsVerified || !state.progress.areRegistersChecked) {
                return RegistrationStep.PersonalDataAfterError;
            }
            if (!state.progress.isEmailVerified) {
                return RegistrationStep.EmailVerification;
            }
            if (!state.progress.areGamingLimitsSet) {
                return RegistrationStep.GamingLimits;
            }
            if (!state.progress.isPersonalDataVerified) {
                if (state.progress.isOcrPending) {
                    return RegistrationStep.OcrPending;
                }
                if (state.progress.isOcrApproved && state.progress.isDepositPending) {
                    return RegistrationStep.OcrDepositPending;
                }
                if (state.progress.isOcrApproved && state.progress.isDepositDeclined) {
                    return RegistrationStep.OcrDepositRejected;
                }
                if (state.progress.isOcrApproved) {
                    return RegistrationStep.OcrComplete;
                }
                return RegistrationStep.PersonalDataVerificationChoice;
            }
            if (!state.progress.isDepositApproved) {
                return RegistrationStep.Deposit;
            }

            return RegistrationStep.Account;
        },
    },
    actions: {
        async loadData() {
            const response = await getPlayerAccount();
            const responseData = response.TempAccountStatus;

            this.user.firstName = responseData.FirstName;
            this.user.lastName = responseData.LastName;
            this.user.street = responseData.Address;
            this.user.city = responseData.City;
            this.user.zipCode = responseData.ZipCode;
            this.user.phone = responseData.Phone?.toString();
            this.user.email = responseData.Email;
            this.user.registrationVertical = responseData.RegistrationVerticalID
                ? (responseData.RegistrationVerticalID as VerticalId)
                : null;
            this.user.isLimitedAccount = responseData.LimitedAccountTypeStatus.toLowerCase() != "none";
            this.user.hid = responseData.HID;
            this.user.citizenship = responseData.Citizenship;

            this.progress.isPersonalDataFilled = !!responseData.FirstName;
            this.progress.isSourceOfFundsVerified = responseData.PlayerComplianceTrackingCDDStatus === "Passed";
            this.progress.areRegistersChecked =
                !!responseData.RegulatorPopulationRegistryStatus &&
                (!!responseData.HID || !!responseData.RegulatorGamblingRegistryStatus);
            this.progress.isBankAccountVerified =
                responseData.PaymentMethodVerificationStatus === PlayerAccountStatusValue.Approved;
            this.progress.isEmailVerified = responseData.EmailVerificationStatus === PlayerAccountStatusValue.Approved;
            this.progress.isPersonalDataVerified =
                responseData.POSVerificationStatus === PlayerAccountStatusValue.Approved;
            this.progress.areGamingLimitsSet =
                responseData.ResponsibleGamingLimitsStatus === PlayerAccountStatusValue.Approved;
            this.progress.isDepositApproved = responseData.HasApprovedDeposits;
            this.progress.isDepositPending = responseData.HasPendingDeposits;
            this.progress.isDepositDeclined = responseData.HasDeclinedDeposits;
            this.progress.isOcrPending =
                responseData.PersonNationalIDVerificationStatus === PlayerAccountStatusValue.Required;
            this.progress.isOcrApproved =
                responseData.PersonNationalIDVerificationStatus === PlayerAccountStatusValue.Approved;

            promiseResolve();
        },

        async setActiveBonus() {
            const { bonuses } = window.registrationConfiguration;
            try {
                const playersBonusIds = await BiRealtimeHttpMetods.getPlayersBonuses();
                this.selectedBonusId = bonuses.find((bonus) => playersBonusIds.includes(bonus.id))?.id;
            } catch {
                // no need to notify user if previously selected bonus is not loaded
            }
        },

        async setActiveStep(isFirstLoad = false) {
            await ngFrameworkLoaded();
            const { isLoggedIn } = useNgSession();
            if (!isLoggedIn.value) {
                this.activeStep = RegistrationStep.Account;
                promiseResolve();
                return;
            }

            try {
                await this.loadData();
            } catch {
                showRegistrationError(RegistrationEcode.GetPlayerDetailFailed);
            }

            if (!this.user.isLimitedAccount) {
                this.activeStep = RegistrationStep.Account;
                return;
            }

            if (isFirstLoad || !this.selectedBonusId) {
                await this.setActiveBonus();
            }

            // finish ocr verification
            if (
                this.user.isLimitedAccount &&
                !this.progress.isPersonalDataVerified &&
                this.progress.isOcrApproved &&
                this.progress.isBankAccountVerified
            ) {
                try {
                    await verifyIdScan();
                    await this.loadData();
                } catch {
                    await cancelIdScan();
                    this.activeStep = RegistrationStep.OcrFailed;
                    return;
                }
            }

            // finish registration
            if (
                this.progress.isPersonalDataVerified &&
                this.progress.isEmailVerified &&
                this.progress.areGamingLimitsSet &&
                this.progress.isDepositApproved
            ) {
                try {
                    await postVerification(this.user.hid, this.user.email);
                    this.activeStep = RegistrationStep.Success;
                    return;
                } catch (e: unknown) {
                    const error = e as AxiosError;
                    if (error.response.status === 451) {
                        useRegistrationStore().setUnsuccessStep(RegistrationUnsuccessReason.Risky);
                        return;
                    }
                    showRegistrationError(RegistrationEcode.LastStepVerificationFailed);
                }
            }

            if (isFirstLoad) {
                this.activeStep = RegistrationStep.Pending;
                return;
            }

            this.activeStep = this.availableStep;
        },

        setUnsuccessStep(error: RegistrationUnsuccessReason) {
            this.unsuccessReason = error;
            this.activeStep = RegistrationStep.Unsuccess;
        },

        setReturnUrl() {
            const returnUrl = UrlUtils.getQueryString("returnUrl");
            if (returnUrl) {
                this.returnUrl = decodeURIComponent(returnUrl);
                UrlUtils.clearUrlParam("returnUrl");
            }
        },
    },
    persist: {
        pick: ["returnUrl", "isWaitingForDeposit", "bankIdSelectedBank", "selectedBonusId"],
        // eslint-disable-next-line no-restricted-globals
        storage: sessionStorage,
    },
});
